import { useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';

const FALLBACK_DELAY_MS = 1000;

/**
 * Focuses an input element after the CSS stagger entrance animation on its
 * wrapper completes. Returns a `wrapperRef` that must be placed on the
 * animated ancestor (e.g. the `answerInputWrapper` div).
 *
 * Falls back to a timeout for users with `prefers-reduced-motion` where the
 * CSS animation never fires.
 */
export function useAutoFocusAfterAnimation(
  getTarget: () => HTMLElement | null | undefined,
  enabled: boolean,
) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const getTargetRef = useRef(getTarget);
  getTargetRef.current = getTarget;

  useEffect(() => {
    if (!enabled || isMobile) return;

    const el = getTargetRef.current();
    if (!el) return;

    let focused = false;
    const focusEl = () => {
      if (focused) return;
      // Inside a multi-question page only the FIRST child should auto-focus.
      // Without this guard every child's screen calls focus() around the
      // same time, the browser scrolls the last-focused element into view,
      // and the page visibly jumps. The first child is marked via
      // `data-mqp-first-child="true"` by MultiQuestionPageScreen.
      const mqpWrapper = el.closest('.mqp-inline');
      if (mqpWrapper && mqpWrapper.getAttribute('data-mqp-first-child') !== 'true') return;
      focused = true;
      el.focus();
    };

    const wrapper = wrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener('animationend', focusEl, { once: true });
    }
    const fallback = setTimeout(focusEl, FALLBACK_DELAY_MS);

    return () => {
      wrapper?.removeEventListener('animationend', focusEl);
      clearTimeout(fallback);
    };
  }, [enabled]);

  return { wrapperRef };
}
